/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase boundary; new tables not yet in generated types. See mem://preferences/lint-debt.md */
// Workspace Manual Destinations — per-workspace list of "copy/paste only"
// publishing destinations (X, LinkedIn, Substack Posts, etc.) used by the
// Publisher chip row. Seeded on first read for new workspaces.

import { supabase } from "@/integrations/supabase/client";
import { ALL_PUBLISH_TYPE_KEYS, type PublishTypeKey } from "@/lib/publishTypes";

export type ManualDestination = {
  id: string;
  workspace_id: string;
  label: string;
  icon_key: string | null;
  position: number;
  applies_to_publish_types: PublishTypeKey[];
  created_at: string;
  updated_at: string;
};

const TBL = "workspace_manual_destinations" as const;

/** Seeded defaults — order matters; matches the Publisher chip order. Each
 *  carries a sensible default applies-to set so freshly-seeded workspaces
 *  immediately have clean per-content-type filtering. */
export const SEED_MANUAL_DESTINATIONS: ReadonlyArray<{
  label: string;
  icon_key: string;
  applies_to: readonly PublishTypeKey[];
}> = [
  { label: "Medium", icon_key: "medium", applies_to: ["article", "book_chapter"] },
  { label: "Substack Posts", icon_key: "substack", applies_to: ["article", "newsletter", "book_chapter"] },
  { label: "LinkedIn", icon_key: "linkedin", applies_to: ["article"] },
  { label: "Facebook", icon_key: "facebook", applies_to: ["article"] },
];

const sanitizeAppliesTo = (raw: unknown): PublishTypeKey[] => {
  if (!Array.isArray(raw)) return [...ALL_PUBLISH_TYPE_KEYS];
  const allow = new Set<string>(ALL_PUBLISH_TYPE_KEYS);
  const cleaned = raw.filter((v): v is PublishTypeKey => typeof v === "string" && allow.has(v));
  return cleaned.length > 0 ? cleaned : [...ALL_PUBLISH_TYPE_KEYS];
};

const mapRow = (row: any): ManualDestination => ({
  ...row,
  applies_to_publish_types: sanitizeAppliesTo(row?.applies_to_publish_types),
});

export const listManualDestinations = async (
  workspaceId: string,
): Promise<ManualDestination[]> => {
  const { data, error } = await (supabase as any)
    .from(TBL)
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as any[]).map(mapRow);
};

/** Insert the seed list if the workspace has none yet. Returns the post-seed list. */
export const ensureSeededManualDestinations = async (
  workspaceId: string,
  createdByUserId: string,
): Promise<ManualDestination[]> => {
  const existing = await listManualDestinations(workspaceId);
  if (existing.length > 0) return existing;
  const rows = SEED_MANUAL_DESTINATIONS.map((d, i) => ({
    workspace_id: workspaceId,
    label: d.label,
    icon_key: d.icon_key,
    position: i,
    applies_to_publish_types: [...d.applies_to],
    created_by_user_id: createdByUserId,
  }));
  const { error } = await (supabase as any).from(TBL).insert(rows);
  if (error) throw error;
  return listManualDestinations(workspaceId);
};

export const createManualDestination = async (input: {
  workspaceId: string;
  label: string;
  iconKey?: string | null;
  position?: number;
  appliesToPublishTypes?: readonly PublishTypeKey[];
  createdByUserId: string;
}): Promise<ManualDestination> => {
  const applies =
    input.appliesToPublishTypes && input.appliesToPublishTypes.length > 0
      ? [...input.appliesToPublishTypes]
      : [...ALL_PUBLISH_TYPE_KEYS];
  const { data, error } = await (supabase as any)
    .from(TBL)
    .insert({
      workspace_id: input.workspaceId,
      label: input.label.trim(),
      icon_key: input.iconKey ?? null,
      position: input.position ?? 999,
      applies_to_publish_types: applies,
      created_by_user_id: input.createdByUserId,
    })
    .select("*")
    .single();
  if (error) throw error;
  return mapRow(data);
};

export const renameManualDestination = async (id: string, label: string): Promise<void> => {
  const { error } = await (supabase as any).from(TBL).update({ label: label.trim() }).eq("id", id);
  if (error) throw error;
};

export const updateManualDestinationAppliesTo = async (
  id: string,
  appliesTo: readonly PublishTypeKey[],
): Promise<void> => {
  if (appliesTo.length === 0) {
    throw new Error("Pick at least one content type.");
  }
  const { error } = await (supabase as any)
    .from(TBL)
    .update({ applies_to_publish_types: [...appliesTo] })
    .eq("id", id);
  if (error) throw error;
};

export const deleteManualDestination = async (id: string): Promise<void> => {
  const { error } = await (supabase as any).from(TBL).delete().eq("id", id);
  if (error) throw error;
};

